// Settings > Help and your data (v4 D6 parity): the taps for four things the Director already does by voice. Each one
// calls the same tool (report_problem, send_feedback, export_my_data, delete_my_data) through tapTool, so the result,
// the email and the Director's context are the same either way. Deleting asks once, plainly, and only emails a link:
// nothing is deleted until that link is opened.
import { useState } from "react";
import { tapTool } from "../actions";
import { toast } from "../store";
import { Icon, Sheet } from "./bits";

/** The tool answers the Director in words; a refusal or a failed email says so ("couldn't", "not started"). */
export const toolWentWrong = (result: string) => /couldn.t|can.t|not started|try again later|too many|a lot of requests/i.test(result);

type Open = "report" | "idea" | "export" | "delete" | null;

const ROWS: { open: Exclude<Open, null>; dataAction: string; icon: string; title: string; hint: string }[] = [
  { open: "report", dataAction: "help.report", icon: "alert", title: "Report a problem", hint: "Something broken, stuck, or a charge that looks wrong" },
  { open: "idea", dataAction: "help.feedback", icon: "chat", title: "Send an idea", hint: "What you wish it could do" },
  { open: "export", dataAction: "account.export", icon: "download", title: "Email me a copy of my data", hint: "A download link, good for 24 hours" },
  { open: "delete", dataAction: "account.delete", icon: "trash", title: "Delete my account", hint: "Everything in it, after you confirm by email" },
];

export function HelpAndData() {
  const [open, setOpen] = useState<Open>(null);
  const close = () => setOpen(null);
  return (
    <section className="group help-data" aria-labelledby="helpdata-h" data-help="settings.help">
      <h2 id="helpdata-h" className="group-title">
        Help and your data
      </h2>
      <p className="muted">Or just tell your Director: “something's broken”, “send me my data”.</p>
      <div className="help-data-rows">
        {ROWS.map((r) => (
          <button key={r.open} type="button" className={`settings-row help-data-row${r.open === "delete" ? " is-danger" : ""}`} data-action={r.dataAction} onClick={() => setOpen(r.open)}>
            <Icon name={r.icon} />
            <span>
              <strong>{r.title}</strong>
              <span className="muted">{r.hint}</span>
            </span>
            <Icon name="back" size={16} />
          </button>
        ))}
      </div>
      {(open === "report" || open === "idea") && <WriteSheet kind={open} onClose={close} />}
      {(open === "export" || open === "delete") && <DataSheet kind={open} onClose={close} />}
    </section>
  );
}

function WriteSheet({ kind, onClose }: { kind: "report" | "idea"; onClose: () => void }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const report = kind === "report";
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    const r = report ? await tapTool("report_problem", { what: t, include_screen: false }, "reported a problem") : await tapTool("send_feedback", { text: t }, "sent an idea");
    setBusy(false);
    if (!r) return;
    if (toolWentWrong(r.result)) return toast("That didn't go through. Try again in a minute.", "bad");
    toast(report ? "Sent to support. They'll email you." : "Sent. Thank you.", "good");
    onClose();
  };
  return (
    <Sheet title={report ? "Report a problem" : "Send an idea"} onClose={onClose}>
      <form
        className="sheet-body"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <label className="field">
          <span className="field-label">{report ? "What went wrong?" : "What would help?"}</span>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={2000} placeholder={report ? "My render has been stuck at 80% for an hour" : "I wish it could make GIFs"} data-autofocus />
          {report && <span className="field-hint">Support gets this with the video you have open and your latest jobs.</span>}
        </label>
        <button type="submit" className="btn btn-primary btn-block" disabled={busy || !text.trim()}>
          {busy ? "Sending" : "Send"}
        </button>
      </form>
    </Sheet>
  );
}

function DataSheet({ kind, onClose }: { kind: "export" | "delete"; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const del = kind === "delete";
  const go = async () => {
    setBusy(true);
    const r = del ? await tapTool("delete_my_data", { confirmed: true }, "asked to delete their account (a confirmation email goes out)") : await tapTool("export_my_data", { confirmed: true }, "asked for a copy of their data");
    setBusy(false);
    if (!r) return;
    if (toolWentWrong(r.result)) return toast("The email couldn't be sent just now. Try again later, or write to privacy@cao.studio.", "bad");
    toast(del ? "Check your email to confirm. Nothing is deleted until you do." : "Check your email for the link.", "good");
    onClose();
  };
  return (
    <Sheet title={del ? "Delete your account?" : "Email you a copy?"} onClose={onClose}>
      <div className="sheet-body confirm">
        <p>
          {del
            ? "This deletes your account and everything in it: your videos, uploads and credits. It can't be undone. We'll email you a link first, and nothing is deleted until you open it and confirm."
            : "We'll email a link to download everything in your account: channels, videos, scripts, characters, memories, purchases and file links. It works for 24 hours."}
        </p>
        <div className="confirm-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose} data-autofocus>
            {del ? "Keep my account" : "Not now"}
          </button>
          <button type="button" className={`btn ${del ? "btn-danger" : "btn-primary"}`} onClick={() => void go()} disabled={busy}>
            {busy ? "Sending" : del ? "Email me the link" : "Email it"}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
